<template>
  <div
    class="relative flex w-full rounded-[28px] items-end px-8 transition-colors duration-300 ease-in-out"
    :class="
      withSuggestions
        ? 'bg-static-default-low'
        : 'bg-static-default-hi [&:hover:not(:focus-within)]:bg-static-default-hi-hover active:bg-static-default-hi-hover'
    "
  >
    <!-- Screen reader only label -->
    <label class="sr-only" for="auto-resize-textarea">{{ label }}</label>

    <!-- Auto-resize Textarea -->
    <div
      ref="textareaContainer"
      class="grid w-full after:invisible after:min-h-[calc(var(--textarea-rows)*1.5em_+_32px)] after:overflow-hidden after:whitespace-pre-wrap after:px-16 after:py-16 after:text-inherit after:content-[attr(data-cloned-val)_'_'] after:line-clamp-[--textarea-rows] after:[grid-area:1/1/2/2] not-supports-[line-clamp-1]:after:max-h-[calc(var(--textarea-rows)*1.5em_+_32px)]"
      data-cloned-val=""
    >
      <textarea
        id="auto-resize-textarea"
        ref="textarea"
        :[SCROLL_CONTAINER_ATTRIBUTE]="''"
        class="w-full resize-none overflow-y-auto overscroll-y-contain bg-transparent outline-none px-8 py-16 [grid-area:1/1/2/2] scrollbar-none placeholder:text-static-default-low align-content-center-safe"
        :disabled
        :placeholder="placeholder ?? label"
        :rows="rowsRef"
        :value
        v-on="textareaState.listeners"
        @keydown="handleKeydown"
      />
    </div>

    <RevButtonIcon
      v-if="!!value"
      :aria-label="buttonLabel"
      class="mb-12 bg-action-ai-hi"
      :disabled
      :icon="IconArrowUp"
      size="small"
      type="submit"
      variant="primary"
    />
  </div>
</template>

<script setup lang="ts">
import { type Ref, nextTick, onMounted, ref, toRef, watch } from 'vue'

import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { useNativeInput } from '@ds/composables/useNativeInput'
import { IconArrowUp } from '@ds/icons/IconArrowUp'
import type { InputValue } from '@ds/types/InputValue'
import { requestAnimationFrame } from '@ds/utils/requestAnimationFrame'
import { watchThrottled } from '@vueuse/core'

import { SCROLL_CONTAINER_ATTRIBUTE } from '../../constants'

const props = defineProps<{
  disabled?: boolean
  label: string
  // Visible textarea placeholder. Defaults to `label` when omitted; pass an
  // empty string to hide the placeholder text while keeping the sr-only
  // label (and the pill itself) rendered, e.g. while the layout is still
  // settling and the placeholder would flash at the wrong position.
  placeholder?: string
  modelValue: InputValue
  buttonLabel: string
  withSuggestions?: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [value: InputValue]
  input: [value: Event]
  submit: []
}>()

const ROW_HEIGHT = 24
// py-16 = 16px top + 16px bottom
const TEXTAREA_PADDING = 32
const MAX_ROWS = 3

const rowsRef = ref(1)
const textareaContainer = ref<HTMLDivElement | null>(null)
const textarea = ref<HTMLInputElement | HTMLTextAreaElement | null>(null)
const value = toRef(props, 'modelValue')
const disabled = toRef(props, 'disabled')

const syncClonedContentWithPseudoElement = (clonedValue: string) => {
  if (!textareaContainer.value) return
  textareaContainer.value.setAttribute('data-cloned-val', clonedValue)
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key !== 'Enter') return
  if (event.shiftKey) return
  const isTouchPrimary = window.matchMedia(
    '(hover: none) and (pointer: coarse)',
  ).matches
  if (isTouchPrimary && !event.metaKey && !event.ctrlKey) return
  event.preventDefault()
  emit('submit')
}

const textareaState = useNativeInput(
  textarea as Ref<HTMLInputElement | HTMLTextAreaElement | null>,
  value,
  {
    onInput: (event) => {
      emit('update:modelValue', event.target.value)
      syncClonedContentWithPseudoElement(event.target.value)
      emit('input', event)
    },
  },
)

function setHeightBasedOnRows(rowsForContent: number) {
  rowsRef.value = Math.min(rowsForContent || 1, MAX_ROWS)

  requestAnimationFrame(() => {
    textareaContainer.value?.style.setProperty(
      '--textarea-rows',
      rowsRef.value.toString(),
    )
  })
}

async function resize() {
  if (!value.value) {
    setHeightBasedOnRows(1)

    return
  }

  // Temporarily collapse the textarea to 1 row in order to read its natural scrollHeight
  rowsRef.value = 1
  textareaContainer.value?.style.setProperty('--textarea-rows', '1')

  // Ensure Vue has flushed before reading scrollHeight
  await nextTick()

  const textAreaScrollHeight =
    textarea.value?.scrollHeight || ROW_HEIGHT + TEXTAREA_PADDING

  const rowsCalc = (textAreaScrollHeight - TEXTAREA_PADDING) / ROW_HEIGHT

  setHeightBasedOnRows(rowsCalc)
}

watchThrottled(
  value,
  () => {
    resize()
  },
  {
    immediate: true,
    flush: 'post',
    throttle: 200,
  },
)

onMounted(() => {
  syncClonedContentWithPseudoElement(String(value.value || ''))

  if (!disabled.value) {
    textarea.value?.focus()
  }
})

// Focusing a disabled textarea is a no-op, so re-focus once it becomes
// enabled (e.g. the assistant session finishes loading). flush: 'post'
// ensures the disabled attribute has already been removed from the DOM.
watch(
  disabled,
  (isDisabled, wasDisabled) => {
    if (wasDisabled && !isDisabled) {
      textarea.value?.focus()
    }
  },
  { flush: 'post' },
)
</script>
